import isEqual from 'lodash.isequal';
import { BehaviorSubject, from, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import {
    catchError,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { logarithmicTime } from 'src/app/@utils/logarithmic-time.helper';

import { BasketActionResult, CxsBasket, CxsBasketLineType, errorMessages, initializeBasket } from '@accelerator/basket';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Basket, BasketData, BasketLine } from '@core/services/basket/basket.model';
import { PlatformService } from '@core/services/platform.service';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { SiteContextService } from '@core/services/site-context.service';
import { StorageService } from '@core/services/storage.service';
import {
    BasketCalculatedLineViewModel as Line,
    GiftCardDetails,
    ValidateCouponCodeResponse
} from '@models/api-orders.models';

import { NotificationService } from '../notification/notification.service';
import {
    BasketEvent,
    cxsBasketToKKBasket,
    filterLines,
    kkBasketToCxsBasket,
    mapToAddRemoveEvent
} from './basket-mapping-utils';
import {
    GiftOptionEvent,
    GiftOptionEventType,
    GiftOptionType
} from '@features/basket/basket-gift-options/basket-gift-options.model';
import { GiftOptionsDialogResponse } from '@features/basket-shared/basket-gift-options-dialog/basket-gift-options-dialog.model';
import { AddToBasketState } from '@features/product/product';
import { GtmService } from '@core/services/gtm/gtm.service';
import { SoftLoginState } from '@features/soft-login/soft-login.models';

const basketStorageKey = 'basket';
const softLoginMemberDataKey = 'membership-data';
const softLoginMemberStoreKey = 'membership-store';

@Injectable({
    providedIn: 'root'
})
export class BasketService implements OnDestroy {
    private basketRef: ReturnType<typeof initializeBasket>;

    private readonly unsubscribe: Subject<void> = new Subject<void>();
    private readonly basketDataRs$ = new ReplaySubject<BasketData>(1);
    private readonly basketActionEventS$ = new Subject<BasketEvent>();
    private readonly errorS$: Subject<HttpErrorResponse | null> = new Subject();

    public readonly basketData$: Observable<BasketData> = this.basketDataRs$.asObservable();
    public readonly basketActionEvent$ = this.basketActionEventS$.asObservable();
    public readonly error$ = this.errorS$.asObservable();
    public readonly zeroPriceOrder$: Observable<boolean> = this.basketData$.pipe(
        map(({ basket }) => Boolean(basket.totalPriceRaw <= 0))
    );

    constructor(
        private storage: StorageService,
        private httpClient: HttpClient,
        private platformService: PlatformService,
        private notificationService: NotificationService,
        private relewiseTrackingService: RelewiseTrackingService,
        private siteContextService: SiteContextService,
        private gtmService: GtmService,
        private ngZone: NgZone
    ) {
        if (this.platformService.isServer) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.initializeBasket();
        });

        this.watchForChanges();

        let basketData: Partial<Basket> = {};
        this.basketData$
            .pipe(
                // Make sure request is done and that we do not track across inactive tabs
                filter(({ requestInProgress, isActiveWindow }) => !requestInProgress && isActiveWindow),
                map(({ basket }) => {
                    // save only the data needed to be used later for the relewise tracking;
                    basketData = {
                        basketId: basket.basketId,
                        totalPriceRaw: basket.totalPriceRaw,
                        lines: basket.lines
                    };
                    return basket.lines as Line[];
                }),
                distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
                pairwise(),
                map(([prev, curr]) => [filterLines(prev, 'Normal'), filterLines(curr, 'Normal')]),
                map(([prev, curr]) => mapToAddRemoveEvent(prev, curr))
            )
            .subscribe(({ action, lines }) => {
                this.basketActionEventS$.next({ action, lines });

                // we only need to track the relewise basket updates when we add or remove products
                if (basketData.basketId && (action === 'add' || action === 'remove')) {
                    this.relewiseTrackingService.trackBasket('Basket', basketData.basketId, {
                        lineItems: basketData.lines as Line[],
                        totalAmount: basketData.totalPriceRaw || 0
                    });
                }

                if (action === 'add') {
                    this.gtmService.trackingEvents.trackAddToBasket(lines);
                    return;
                }

                if (action === 'remove') {
                    this.gtmService.trackingEvents.trackRemoveFromBasket(lines);
                }
            });

        this.errorS$.pipe(filter(error => !!error)).subscribe(error => {
            const errorArray: string[] = Object.values(error?.error || {}).filter(
                val => typeof val === 'string'
            ) as string[];

            this.basketActionEventS$.next({ action: 'error', lines: [] });

            if (errorArray?.length) {
                this.notificationService.createNotification({
                    messageArray: errorArray,
                    fadeTime: logarithmicTime(errorArray.length, 8000),
                    type: 'warning'
                });
                return;
            }

            this.notificationService.createNotification({
                translationHeadline: 'general.validation.generic',
                translationText: 'basket.genericBasketError',
                fadeTime: 8000,
                type: 'warning'
            });
        });
    }

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.basketRef?.destroy();
    }

    public addToBasket(productId: string, quantityToAdd: number, options?: GiftOptionsDialogResponse): void {
        this.handleActionResult(
            this.basketRef.addToBasket(
                productId,
                quantityToAdd,
                options?.giftWrappingId,
                options?.giftCardId,
                options?.giftCardText
            )
        );
    }

    public removeFromBasket(productId: string): void {
        this.handleActionResult(this.basketRef.removeFromBasket(productId));
    }

    public clearAllBasket(): void {
        const { lines = [] } = this.basketRef.getBasket()?.current || {};
        lines.forEach(line => this.handleActionResult(this.basketRef.removeFromBasket(line.variantNo)));
    }

    public setZipCode(zipCode: string): void {
        this.handleActionResult(this.basketRef?.setZipCode(zipCode));
    }

    public setDeliveryMethod(deliveryMethod: string): void {
        this.handleActionResult(this.basketRef?.setDeliveryMethod(deliveryMethod));
    }

    public changeGiftOptions(eventType: GiftOptionEventType, productId: string): void {
        const { type, event, value } = eventType;
        this.handleGiftWrappingOptions(
            event,
            type,
            productId,
            value?.giftWrappingId,
            value?.giftCardId,
            value?.giftCardText
        );
    }

    public addCouponCode(code: string): Observable<boolean> {
        return this.handleActionResult(this.basketRef.addCouponCode(code));
    }

    public removeCouponCode(code: string): void {
        this.handleActionResult(this.basketRef.removeCouponCode(code));
    }

    public checkCouponCode(code: string): Observable<ValidateCouponCodeResponse> {
        const sanitizedCode = code.toLocaleUpperCase().trim();
        const basketId = this.basketRef.getBasket().current?.basketId;
        return this.httpClient.get<ValidateCouponCodeResponse>(
            `orders-ms/CouponCode/validate/${basketId}/${sanitizedCode}`
        );
    }

    public addGiftCard(code: string): Observable<boolean> {
        return this.handleActionResult(this.basketRef.addGiftCard(code));
    }

    public removeGiftCard(code: string): Observable<boolean> {
        return this.handleActionResult(this.basketRef.removeGiftCard(code));
    }

    public checkGiftCardCode(code: string): Observable<GiftCardDetails> {
        const sanitizedCode = code.toLocaleUpperCase().trim();
        return this.httpClient.get<GiftCardDetails>(`orders-ms/giftcard/validate/${sanitizedCode}`);
    }

    public getUnfinishedBasket(basketId: string): void {
        if (this.basketRef?.getBasket().current?.basketId !== basketId) {
            this.purgeBasket(basketId);
        }
    }

    public watchBasketNotifications(buttonState: BehaviorSubject<AddToBasketState>): Observable<AddToBasketState> {
        return this.basketData$.pipe(
            withLatestFrom(buttonState),
            filter(([response, state]) => !response.requestInProgress && state === 'loading'),
            map(([_, state]) => state),
            catchError(error => throwError(error)),
            tap(
                _ => {
                    buttonState.next('success');

                    this.notificationService.createNotification({
                        type: 'success',
                        translationHeadline: 'general.feedback.notification.addToBasketSuccess'
                    });
                },
                error => {
                    buttonState.next('error');
                    this.notificationService.createNotification({
                        type: 'error',
                        translationHeadline: 'general.feedback.notification.addToBasketError',
                        ...(error.message ? { messageText: error.message } : {})
                    });
                }
            ),
            withLatestFrom(buttonState),
            map(([_, state]) => state),
            filter(([_, state]) => state !== 'idle' && state !== 'loading'),
            delay(3000)
        );
    }

    public setUsePoints(usePoints: boolean): Observable<boolean> {
        const action = this.basketRef.setUsePoints(usePoints);
        this.handleActionResult(action);
        return from(action.synchronized).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    public setMembershipEmail(data: Partial<SoftLoginState>): Observable<boolean> {
        if (this.storage.isStorageSupported) {
            const currentData: SoftLoginState = this.storage.get(softLoginMemberDataKey) ?? {
                email: '',
                status: 'unknown'
            };

            const updatedData = { ...currentData, ...data };
            this.storage.set(softLoginMemberDataKey, updatedData, { sessionOnly: true });
        }

        this.basketRef?.updateMembershipEmail(data?.email ?? '');
        const action = this.basketRef?.updateBasket(data?.email ?? '');
        if (action) {
            this.handleActionResult(action);
            return from(action?.synchronized).pipe(
                map(() => true),
                catchError(() => of(false))
            );
        }
        return of(false);
    }

    public getMembershipData(): SoftLoginState | undefined {
        return this.storage.get(softLoginMemberDataKey);
    }

    public getMembershipEmail(): string | undefined {
        return this.storage.get(softLoginMemberDataKey)?.email;
    }

    public setMembershipStoreId(storeId?: string): void {
        if (this.storage.isStorageSupported) {
            this.storage.set(softLoginMemberStoreKey, storeId, { sessionOnly: true });
        }
    }

    public getMembershipStoreId(): string | undefined {
        return this.storage.get(softLoginMemberStoreKey);
    }

    public clearMembershipData(triggerUpdate: boolean = true): void {
        if (this.storage.isStorageSupported) {
            this.storage.remove(softLoginMemberDataKey);
            this.storage.remove(softLoginMemberStoreKey);
        }

        if (triggerUpdate) {
            this.setMembershipEmail({ email: '', status: 'unknown' });
        }
    }

    private handleGiftWrappingOptions(
        event: GiftOptionEvent,
        type: GiftOptionType,
        productId: string,
        giftWrappingId?: string,
        giftCardId?: string,
        giftCardText?: string
    ): void {
        if (event === 'add') {
            this.handleActionResult(
                this.basketRef.addGiftWrappingOptions(productId, giftWrappingId, giftCardId, giftCardText)
            );
            return;
        }

        if (event === 'update') {
            this.handleActionResult(
                this.basketRef.updateGiftWrappingOptions(productId, giftWrappingId, giftCardId, giftCardText)
            );
            return;
        }

        const lineType = this.getLineType(type);
        if (lineType) {
            this.handleActionResult(this.basketRef.removeGiftWrappingOptions(productId, lineType));
        }
    }

    /**
     * Removes basket from persistent local storage and requests a new one
     * If a new basket id is provided it'll use it for fetching a specific existing basket
     * @param basketId
     */
    private purgeBasket(basketId?: string): void {
        const currentBasket = this.basketRef.getBasket().current;
        if (currentBasket) {
            this.basketDataRs$.next({
                basket: currentBasket,
                requestInProgress: true,
                isActiveWindow: this.siteContextService.isActiveWindow()
            });
        }
        this.unsubscribe.next();
        if (this.storage.isStorageSupported) {
            this.storage.remove(basketStorageKey);
        }
        this.basketRef.destroy();

        this.ngZone.runOutsideAngular(() => {
            this.initializeBasket(basketId);
        });
    }

    /**
     * Initializes the basket and sets the basket data observable since this method because of all the complexity of it, instead of
     * wrap all the content of the method inside ngZone we just wrap the call to the method, this way we can avoid the change detection
     * and the issue with the hydration not happening on the required time
     * @param basketId
     * @private
     */
    private initializeBasket(basketId?: string): void {
        const basketFromStorage = this.getBasketFromStorage();
        if (basketFromStorage && (!basketId || basketId === basketFromStorage.basketId)) {
            this.basketDataRs$.next({
                basket: cxsBasketToKKBasket(basketFromStorage),
                requestInProgress: true,
                isActiveWindow: this.siteContextService.isActiveWindow()
            });
        }

        const existingBasket =
            basketId && basketId !== basketFromStorage?.basketId ? this.createEmptyBasket(basketId) : basketFromStorage;

        this.basketRef = initializeBasket({
            window: this.platformService.isBrowser ? window : undefined,
            httpClient: this.httpClient,
            existingBasket,
            getLatestModifiedDate: () => this.getBasketFromStorage()?.lastModifiedDate || '',
            customerEmail: this.getMembershipEmail()
        });

        if (this.storage.isStorageSupported) {
            this.basketData$.pipe(takeUntil(this.unsubscribe)).subscribe(({ basket }) => {
                if (
                    !basket.lastModifiedDate ||
                    basket.lastModifiedDate !== this.getBasketFromStorage()?.lastModifiedDate
                ) {
                    this.storage.set<CxsBasket>(basketStorageKey, kkBasketToCxsBasket(basket));
                }
            });
        }

        this.basketData$
            .pipe(
                takeUntil(this.unsubscribe),
                debounceTime(1000),
                filter(({ requestInProgress }) => !requestInProgress)
            )
            .subscribe(({ basket }) => {
                basket.couponCodeDiscounts
                    ?.filter(({ couponCodeType }) => couponCodeType === 'Missing')
                    .forEach(couponCodeDiscount => {
                        this.notificationService.createNotification({
                            translationHeadline: 'discountCode.expiredCodeNotificationHeadline',
                            messageText: couponCodeDiscount.couponCode,
                            fadeTime: 8000
                        });
                        this.removeCouponCode(couponCodeDiscount.couponCode);
                    });
                basket.giftCardDiscounts
                    ?.filter(({ giftCardType }) => giftCardType === 'Missing')
                    .forEach(giftCardDiscount => {
                        this.notificationService.createNotification({
                            translationHeadline: 'basket.expiredGiftCardNotificationHeadline',
                            messageText: giftCardDiscount.code,
                            fadeTime: 8000
                        });
                        this.removeGiftCard(giftCardDiscount.code);
                    });

                if (basket.errorMessages) {
                    this.errorS$.next(new HttpErrorResponse({ error: basket.errorMessages }));
                }

                const outOfStockRangeLines = basket.lines.find(({ lineType }) => lineType === 'OutOfStockRange');

                // If any lines are out of stock range, we adjust the line, so it fits with what is in stock
                // If there is 0 in stock, we remove the line entirely
                if (outOfStockRangeLines) {
                    const linesWithFixedQuantities = basket.lines.reduce((acc, line) => {
                        const { quantity, stockQuantity, lineType } = line;
                        if (quantity <= 0 || stockQuantity <= 0) {
                            return acc;
                        }
                        acc.push({
                            ...line,
                            ...(lineType === 'OutOfStockRange' ? { quantity: stockQuantity } : {})
                        });
                        return acc;
                    }, [] as BasketLine[]);

                    this.basketRef
                        .reSynchronize(
                            kkBasketToCxsBasket({
                                ...basket,
                                lines: linesWithFixedQuantities
                            }),
                            true
                        )
                        .synchronized.then(syncedBasket => {
                            this.basketDataRs$.next({
                                basket: cxsBasketToKKBasket(syncedBasket),
                                requestInProgress: this.basketRef.hasRequestInProgress(),
                                isActiveWindow: this.siteContextService.isActiveWindow()
                            });
                        });
                }
            });

        this.basketRef.loadComplete
            .then(basket => {
                if (basket) {
                    this.basketDataRs$.next({
                        basket: cxsBasketToKKBasket(basket),
                        requestInProgress: false,
                        isActiveWindow: this.siteContextService.isActiveWindow()
                    });
                }
            })
            .catch(reason => {
                if (reason !== errorMessages.asyncOperationCancelled) {
                    throw reason; // TODO: handle basket failure
                }
            });
    }

    private watchForChanges(): void {
        if (!this.platformService.isBrowser) {
            return;
        }
        if (this.storage.isStorageSupported) {
            const changesSubject = this.storage.getExternalChanges();
            changesSubject.pipe(debounceTime(500)).subscribe(changes => {
                if (changes.basket?.newValue) {
                    const basket = JSON.parse(changes.basket.newValue).value;
                    if (basket.lastModifiedDate !== this.basketRef.getBasket().current?.lastModifiedDate) {
                        const membershipEmail = this.getMembershipEmail();
                        this.handleActionResult(this.basketRef.reSynchronize(basket, false, membershipEmail));
                    }
                }
            });
        }
    }

    private getBasketFromStorage(): CxsBasket | undefined {
        return this.storage.get<CxsBasket>(basketStorageKey);
    }

    /**
     * @param actionResult local update of a basket (can be undefined) and promise of basket after synchronization with server
     * @returns observable for success (true) or failure (false) after synchronization with server
     */
    private handleActionResult(actionResult: BasketActionResult): Observable<boolean> {
        if (actionResult?.localUpdate) {
            this.basketDataRs$.next({
                basket: cxsBasketToKKBasket(actionResult.localUpdate),
                requestInProgress: this.basketRef.hasRequestInProgress(),
                isActiveWindow: this.siteContextService.isActiveWindow()
            });
        }

        const membershipEmail = this.getMembershipEmail();

        if (actionResult?.synchronized) {
            actionResult.synchronized
                .then(basket => {
                    this.errorS$.next(null);
                    this.basketDataRs$.next({
                        basket: cxsBasketToKKBasket(basket),
                        requestInProgress: this.basketRef.hasRequestInProgress(),
                        isActiveWindow: this.siteContextService.isActiveWindow()
                    });
                })
                .catch(error => {
                    // If an error happens while doing requests to server, next the error and resync basket
                    this.errorS$.next(error);
                    this.basketRef
                        .reSynchronize(this.basketRef.getBasket().current, false, membershipEmail)
                        .synchronized.then(syncedBasket => {
                            this.basketDataRs$.next({
                                basket: cxsBasketToKKBasket(syncedBasket),
                                requestInProgress: this.basketRef.hasRequestInProgress(),
                                isActiveWindow: this.siteContextService.isActiveWindow()
                            });
                        });
                });
        }
        return from(actionResult?.synchronized ?? []).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    private createEmptyBasket(basketId: string): CxsBasket {
        return {
            basketId,
            deliveryPriceExcludingVat: '',
            deliveryPrice: '',
            totalLinesPrice: '',
            totalLinesPriceExcludingVat: '',
            totalLinesDiscountAmount: '',
            vatAmount: '',
            totalPriceExcludingVat: '',
            totalPrice: '',
            remainingForFreeDelivery: '',
            totalLinesGiftWrappingPriceExcludingVat: '',
            totalLinesGiftWrappingPrice: '',
            couponCodeDiscounts: [],
            giftCardDiscounts: [],
            couponCodes: [],
            giftCardCodes: [],
            lines: [],
            totalPointsToBeEarned: 0,
            totalPointsDiscount: '',
            totalPointsDiscountRaw: 0,
            totalPointsUsedForDiscount: 0,
            totalLinesPriceBeforeDiscount: '',
            totalLinesPriceBeforeDiscountRaw: 0,
            totalLinesGiftWrappingsCardPrice: '',
            totalLinesGiftWrappingsCardPriceExcludingVat: '',
            totalPriceRaw: 0,
            totalLinesDiscountAmountRaw: 0,
            totalLinesGiftWrappingsCardPriceRaw: 0,
            totalLinesGiftWrappingsCardPriceExcludingVatRaw: 0
        };
    }

    private getLineType(type: GiftOptionType): CxsBasketLineType | undefined {
        if (type === 'wrapping') {
            return 'GiftWrapping';
        }

        if (type === 'card') {
            return 'GiftWrappingCard';
        }

        return;
    }
}
